import { useParams } from 'react-router-dom';

import { ZERO } from '@/constants';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableContainer,
} from '@/ui';

import OpsMembersHeader from './OpsMembersHeader';
import OpsMembersList from './OpsMembersList';

import {
  COHORT_TAG_IDS_KEY,
  MEMBER_STATUSES_KEY,
  SEAT_TYPES_KEY,
  useCompanyMembersParams,
} from '@/features/companyDashboard/common/members/useCompanyMembersParams';
import { useMembersFilterParams } from '@/features/companyDashboard/common/members/useMembersFilterParams';
import { useMembersTableState } from '@/features/companyDashboard/common/members/useMembersTableState';
import FilterDropdown from '@/features/membersTable/FilterDropdown';
import { useCompanyMembers } from '@/hooks/useCompany';
import { useGetCompanyCohortTagsQuery } from '@/store/api/cohortTagApi';

const OpsMembers = () => {
  const {
    handleLimitChange,
    handlePageChange,
    handleSort,
    isPastMembers,
    limit,
    page,
    sortDirection,
    sortField,
    validLimits,
  } = useMembersTableState();
  const { companyId = '' } = useParams();
  const { sessionFilter, setArrayFilter, setSessionFilter } =
    useMembersFilterParams();
  const membersParams = useCompanyMembersParams('ops');
  const { data: cohortTags = [] } = useGetCompanyCohortTagsQuery(companyId, {
    skip: !companyId,
  });

  const memberStatusFilter = membersParams.memberStatuses ?? [];
  const seatTypeFilter = membersParams.seatTypes ?? [];
  const cohortFilter = membersParams.cohortTagIds ?? [];

  const { data, isLoading, isFetching } = useCompanyMembers(
    companyId,
    !isPastMembers,
    page,
    limit,
    sortField,
    sortDirection,
    undefined,
    {
      chemCall: membersParams.chemCall,
      cohortTagIds: cohortFilter,
      memberStatuses: memberStatusFilter,
      noUpcomingSessions: membersParams.noUpcomingSessions,
      scheduledSessions: membersParams.scheduledSessions,
      seatTypes: seatTypeFilter,
    },
  );
  const members = data?.items || [];
  const facets = data?.facets;

  return (
    <div className="flex flex-1 flex-col pt-4">
      <div className="mb-3 flex items-center justify-end gap-3">
        <span className="material-icons text-lg text-cement-400">
          filter_list
        </span>
        <FilterDropdown
          label="Status"
          options={facets?.memberStatuses ?? []}
          selected={memberStatusFilter}
          onChange={(selected) => setArrayFilter(MEMBER_STATUSES_KEY, selected)}
        />
        <FilterDropdown
          label="Seat Type"
          options={facets?.seatTypes ?? []}
          selected={seatTypeFilter}
          onChange={(selected) => setArrayFilter(SEAT_TYPES_KEY, selected)}
        />
        <FilterDropdown
          label="Cohort"
          options={[{ id: 'none', name: 'None' }, ...cohortTags]}
          selected={cohortFilter}
          onChange={(selected) => setArrayFilter(COHORT_TAG_IDS_KEY, selected)}
        />
        <FilterDropdown
          label="Sessions"
          options={facets?.sessionFilters ?? []}
          selected={sessionFilter}
          onChange={setSessionFilter}
        />
      </div>
      <TableContainer className="min-w-[1600px]">
        <Table aria-label="ops company members table">
          <OpsMembersHeader
            sortDirection={sortDirection}
            sortField={sortField}
            onSort={handleSort}
          />
          <OpsMembersList
            companyId={companyId}
            isLoading={isLoading}
            isRefreshing={isFetching}
            members={members}
          />
        </Table>
      </TableContainer>
      <div className="mt-2.5 flex items-center justify-between">
        <FormControl className="min-w-32">
          <InputLabel id="page-size-select-label">Per page</InputLabel>
          <Select
            id="page-size-select"
            label="Per page"
            labelId="page-size-select-label"
            value={limit}
            onChange={handleLimitChange}
          >
            {validLimits.map((pageLimit) => (
              <MenuItem key={pageLimit} value={pageLimit}>
                {pageLimit}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Pagination
          count={data?.pages ?? ZERO}
          page={page}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default OpsMembers;
